import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Leaf, User, Mail, Lock, Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) { setError('Please fill in all fields'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <button onClick={() => navigate('/')} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <ArrowLeft size={24} color={COLORS.textPrimary} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Leaf size={48} color={COLORS.primary} />
          <h1 style={{ fontSize: 32, fontWeight: 800, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>Create Account</h1>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 6 }}>Begin your longevity journey</p>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#FDE8E8', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <AlertCircle size={18} color={COLORS.error} />
            <span style={{ fontSize: 14, color: COLORS.error }}>{error}</span>
          </div>
        )}



        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Full Name</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <User size={20} color={COLORS.textSecondary} />
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Email</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Mail size={20} color={COLORS.textSecondary} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Password</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Lock size={20} color={COLORS.textSecondary} />
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {showPassword ? <EyeOff size={20} color={COLORS.textSecondary} /> : <Eye size={20} color={COLORS.textSecondary} />}
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Confirm Password</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Lock size={20} color={COLORS.textSecondary} />
              <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            </div>
          </div>

          <button type="submit" disabled={loading} style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px', border: 'none', cursor: 'pointer', marginTop: 8, opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s' }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#FFF' }}>{loading ? 'Creating Account...' : 'Create Account'}</span>
          </button>

          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <span style={{ fontSize: 15, color: COLORS.textSecondary }}>Already have an account? </span>
            <Link to="/login" style={{ fontSize: 15, fontWeight: 700, color: COLORS.primary, textDecoration: 'none' }}>Sign In</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
